import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { useT } from "@/lib/LanguageContext";

const CONTACT_EMAIL = "hello@veronikaconk.com";

export default function IntakeForm() {
  const { t, lang } = useT();
  const f = t.form;

  const initial = {
    name: "",
    email: "",
    country: "",
    language: lang === "en" ? "en" : "es",
    situation: "",
    helpType: "",
    message: "",
    linkedin: "",
    consent: false,
  };

  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const setField = (k) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [k]: value }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = f.validation.required;
    if (!form.email.trim()) next.email = f.validation.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      next.email = f.validation.email;
    if (!form.country.trim()) next.country = f.validation.required;
    if (!form.situation) next.situation = f.validation.required;
    if (!form.helpType) next.helpType = f.validation.required;
    if (!form.message.trim()) next.message = f.validation.required;
    if (!form.consent) next.consent = f.validation.consent;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildMailto = () => {
    const langLabel = form.language === "en" ? f.fields.langEn : f.fields.langEs;
    const subject = `${f.mailtoSubject} - ${form.name}`;
    const lines = [
      `${f.bodyLabels.name}: ${form.name}`,
      `${f.bodyLabels.email}: ${form.email}`,
      `${f.bodyLabels.country}: ${form.country}`,
      `${f.bodyLabels.language}: ${langLabel}`,
      `${f.bodyLabels.situation}: ${form.situation}`,
      `${f.bodyLabels.helpType}: ${form.helpType}`,
      "",
      `${f.bodyLabels.message}:`,
      form.message,
      "",
      `${f.bodyLabels.linkedin}: ${form.linkedin || "—"}`,
      `${f.bodyLabels.consent}: ${f.bodyLabels.consentYes}`,
    ];
    const body = lines.join("\n");
    return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitted(true);
    // Open mail client with prefilled draft
    const url = buildMailto();
    setTimeout(() => {
      window.location.href = url;
    }, 250);
  };

  return (
    <section
      id="contacto"
      data-testid="intake-form-section"
      className="relative py-24 md:py-36 bg-[#F7F3F7]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-12">
          {/* LEFT: Header */}
          <div className="col-span-12 md:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.24em] text-[#6B4E6F] mb-6">
              {f.eyebrow}
            </p>
            <h2
              data-testid="form-headline"
              className="font-serif font-light text-[clamp(2.2rem,4.6vw,4.2rem)] leading-[1] tracking-tight"
            >
              {f.title}
            </h2>
            <p className="mt-8 text-base md:text-lg text-[#6B4E6F] leading-relaxed max-w-md">
              {f.subtitle}
            </p>
          </div>

          {/* RIGHT: Form / Success */}
          <div className="col-span-12 md:col-span-7">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="form-success"
                className="border vk-line-strong p-10 md:p-14 bg-[#F7F3F7]"
              >
                <Check
                  size={36}
                  strokeWidth={1.2}
                  className="text-[#E0B25B] mb-6"
                />
                <h3 className="font-serif font-light text-3xl md:text-4xl leading-tight tracking-tight">
                  {lang === "en" ? "Almost there." : "Casi listo."}
                </h3>
                <p className="mt-6 text-base leading-relaxed text-[#6B4E6F]">
                  {f.success}
                </p>
                <div className="mt-10 flex flex-col sm:flex-row gap-4">
                  <a
                    href={buildMailto()}
                    data-testid="form-mailto-link"
                    className="group inline-flex items-center justify-center gap-3 bg-[#4B2F4F] text-[#F7F3F7] px-8 py-4 text-[12px] uppercase tracking-[0.24em] font-medium hover:bg-[#E0B25B] transition-colors duration-300"
                  >
                    {lang === "en" ? "Open email draft" : "Abrir el correo"}
                    <ArrowRight size={16} strokeWidth={1.4} />
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      setForm(initial);
                      setSubmitted(false);
                    }}
                    data-testid="form-reset"
                    className="text-[11px] uppercase tracking-[0.22em] text-[#6B4E6F] hover:text-[#4B2F4F] transition-colors"
                  >
                    {lang === "en"
                      ? "Send another request"
                      : "Enviar otra solicitud"}
                  </button>
                </div>
              </motion.div>
            ) : (
              <form
                noValidate
                onSubmit={onSubmit}
                data-testid="intake-form"
                className="space-y-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field
                    id="name"
                    label={f.fields.name}
                    placeholder={f.fields.namePh}
                    value={form.name}
                    onChange={setField("name")}
                    error={errors.name}
                    testid="form-name"
                  />
                  <Field
                    id="email"
                    type="email"
                    label={f.fields.email}
                    placeholder={f.fields.emailPh}
                    value={form.email}
                    onChange={setField("email")}
                    error={errors.email}
                    testid="form-email"
                  />
                </div>

                <Field
                  id="country"
                  label={f.fields.country}
                  placeholder={f.fields.countryPh}
                  value={form.country}
                  onChange={setField("country")}
                  error={errors.country}
                  testid="form-country"
                />

                <fieldset>
                  <legend className="text-[11px] uppercase tracking-[0.24em] text-[#6B4E6F]">
                    {f.fields.language}
                  </legend>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {["es", "en"].map((opt) => (
                      <label
                        key={opt}
                        className={`cursor-pointer border vk-line-strong px-5 py-3 text-sm uppercase tracking-[0.18em] transition-colors duration-300 ${
                          form.language === opt
                            ? "bg-[#4B2F4F] text-[#F7F3F7]"
                            : "hover:bg-[#4B2F4F]/5"
                        }`}
                      >
                        <input
                          type="radio"
                          name="language"
                          value={opt}
                          checked={form.language === opt}
                          onChange={setField("language")}
                          className="sr-only"
                          data-testid={`form-language-${opt}`}
                        />
                        {opt === "es" ? f.fields.langEs : f.fields.langEn}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <SelectField
                  id="situation"
                  label={f.fields.situation}
                  placeholder={f.fields.situationPh}
                  value={form.situation}
                  onChange={setField("situation")}
                  options={f.fields.situations}
                  error={errors.situation}
                  testid="form-situation"
                />

                <SelectField
                  id="helpType"
                  label={f.fields.helpType}
                  placeholder={f.fields.helpTypePh}
                  value={form.helpType}
                  onChange={setField("helpType")}
                  options={f.fields.helpTypes}
                  error={errors.helpType}
                  testid="form-helpType"
                />

                <div>
                  <label
                    htmlFor="message"
                    className="text-[11px] uppercase tracking-[0.24em] text-[#6B4E6F]"
                  >
                    {f.fields.message}
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    value={form.message}
                    onChange={setField("message")}
                    placeholder={f.fields.messagePh}
                    data-testid="form-message"
                    className="vk-input"
                    aria-invalid={!!errors.message}
                  />
                  {errors.message && (
                    <p className="mt-2 text-xs text-[#E0B25B]">{errors.message}</p>
                  )}
                </div>

                <Field
                  id="linkedin"
                  type="url"
                  label={f.fields.linkedin}
                  placeholder={f.fields.linkedinPh}
                  value={form.linkedin}
                  onChange={setField("linkedin")}
                  testid="form-linkedin"
                />

                <div>
                  <label className="flex items-start gap-4 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={form.consent}
                      onChange={setField("consent")}
                      data-testid="form-consent"
                      className="mt-1 w-5 h-5 accent-[#E0B25B] flex-shrink-0"
                    />
                    <span className="text-sm leading-relaxed text-[#6B4E6F]">
                      {f.fields.consent}
                    </span>
                  </label>
                  {errors.consent && (
                    <p className="mt-2 text-xs text-[#E0B25B]">{errors.consent}</p>
                  )}
                </div>

                <button
                  type="submit"
                  data-testid="form-submit"
                  className="group inline-flex items-center gap-3 bg-[#4B2F4F] text-[#F7F3F7] px-10 py-5 text-[12px] uppercase tracking-[0.24em] font-medium hover:bg-[#E0B25B] transition-colors duration-300"
                >
                  {f.submit}
                  <ArrowRight
                    size={16}
                    strokeWidth={1.4}
                    className="group-hover:translate-x-1 transition-transform duration-300"
                  />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, type = "text", placeholder, value, onChange, error, testid }) {
  return (
    <div>
      <label htmlFor={id} className="text-[11px] uppercase tracking-[0.24em] text-[#6B4E6F]">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        aria-invalid={!!error}
        className="vk-input"
      />
      {error && <p className="mt-2 text-xs text-[#E0B25B]">{error}</p>}
    </div>
  );
}

function SelectField({ id, label, placeholder, value, onChange, options, error, testid }) {
  return (
    <div>
      <label htmlFor={id} className="text-[11px] uppercase tracking-[0.24em] text-[#6B4E6F]">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={onChange}
        data-testid={testid}
        aria-invalid={!!error}
        className="vk-input appearance-none cursor-pointer"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%231A1A1A' stroke-width='1.4' d='M1 1l5 5 5-5'/%3E%3C/svg%3E\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 0.25rem center",
          paddingRight: "1.5rem",
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((opt, i) => (
          <option key={i} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      {error && <p className="mt-2 text-xs text-[#E0B25B]">{error}</p>}
    </div>
  );
}
